/**
 * Mapper identity store.
 *
 * Holds the currently signed-in mapper. The token is a signed JWT emitted by
 * the backend (see backend/app/auth_api.py); `api/client.ts` reads it from
 * here and attaches it as `Authorization: Bearer …` on every API call.
 * Persisted to localStorage under `synapse:auth`.
 */

import { create } from 'zustand';

export interface Mapper {
  id: string;
  display_name: string;
  role: string;
  /** Effective allowed app ids (see Dashboard EDITORS `view`). Undefined on
   *  older sessions and admins — treated as "all apps" downstream. */
  apps?: string[];
}

const STORAGE_KEY = 'synapse:auth';

interface Stored {
  mapper: Mapper | null;
  token: string | null;
}

function load(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { mapper: null, token: null };
    const p = JSON.parse(raw);
    return { mapper: p.mapper ?? null, token: p.token ?? null };
  } catch {
    return { mapper: null, token: null };
  }
}

function persist(s: Stored) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

interface AuthState {
  mapper: Mapper | null;
  token: string | null;
  setSession: (mapper: Mapper, token: string) => void;
  logout: () => void;
  /** Convenience: the id used to tag knowledge events, or undefined if signed out. */
  mapperId: () => string | undefined;
}

export const useAuthStore = create<AuthState>((set, get) => {
  const initial = load();
  return {
    mapper: initial.mapper,
    token: initial.token,
    setSession: (mapper, token) => { persist({ mapper, token }); set({ mapper, token }); },
    logout: () => { persist({ mapper: null, token: null }); set({ mapper: null, token: null }); },
    mapperId: () => get().mapper?.id,
  };
});
